// Parecidos (§4.2, §7.6): imagens do próprio acervo com a mesma atmosfera, numa folha por cima do detalhe.
// Abaixo de SIMILAR_MIN imagens analisadas a folha explica quantas faltam, sem chamar o servidor.
import { useEffect, useRef, useState } from "preact/hooks";
import type { Card } from "../../../../shared/types.ts";
import { Grid } from "../Grid.tsx";
import { IconClose, IconSimilar, IconSpin } from "../Icons.tsx";
import { quietIngest } from "../lib.ts";
import { byId, showCard, store } from "../state.ts";
import { openOver } from "./Extras.tsx";

export const SIMILAR_MIN = 20;
// Sem a chave da IA o vetor de uma imagem é só o nome e as etiquetas: não conta como analisada (09-PARIDADE §6.1).
const analyzed = (c: Card) => c.kind === "image" && !c.isPrivate && (c.ingestState === "ready" || (c.ingestState === "partial" && !quietIngest(c)));

export function SimilarButton({ card: c, onDark }: { card: Card; onDark: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" class={`similar rd-similar${onDark ? " on-dark" : ""}`} aria-haspopup="dialog" data-tip="Imagens com a mesma atmosfera" onClick={() => setOpen(true)}>
        <IconSimilar />
        Parecidos
      </button>
      {open && <SimilarSheet card={c} onClose={() => setOpen(false)} />}
    </>
  );
}

function SimilarSheet({ card: c, onClose }: { card: Card; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [ids, setIds] = useState<string[] | null>(null);
  const [error, setError] = useState(false);
  const missing = SIMILAR_MIN - store.cards.value.filter(analyzed).length;
  useEffect(() => openOver(ref.current!), []);
  useEffect(() => {
    if (missing > 0) return;
    let on = true;
    store.similar(c.id, 24).then((r) => on && setIds(r), () => on && setError(true));
    return () => void (on = false);
  }, []);
  const map = byId.value;
  const list = (ids ?? []).map((id) => map.get(id)).filter((x): x is Card => !!x && !x.deletedAt && !x.isPrivate && x.id !== c.id);
  const close = () => ref.current?.close();
  return (
    <dialog
      ref={ref}
      class="rd-sheet"
      aria-labelledby="similar-title"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClose={onClose}
      onClick={(e) => {
        // Card da grade: fecha a folha (a entrada dela sai do histórico) e troca o card do detalhe sem empilhar outra.
        const a = (e.target as Element).closest<HTMLAnchorElement>("a.card-open");
        if (!a || e.defaultPrevented || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        const id = decodeURIComponent(a.hash.slice(1));
        if (history.state?.layer) addEventListener("popstate", () => showCard(id), { once: true });
        else showCard(id);
        close();
      }}
    >
      <header class="rd-sheet-head">
        <IconSimilar />
        <div>
          <h2 id="similar-title">Parecidos</h2>
          <p class="rd-sheet-sub">Do seu acervo, com a mesma atmosfera de “{c.title ?? "esta imagem"}”.</p>
        </div>
        <button type="button" class="rd-x" aria-label="Fechar Parecidos" onClick={close}>
          <IconClose />
        </button>
      </header>
      {missing > 0 ? (
        <div class="empty rd-sheet-msg">
          <p class="empty-title">Ainda poucas imagens.</p>
          <p class="empty-line">
            Parecidos compara a imagem com pelo menos {SIMILAR_MIN} imagens já analisadas do seu acervo. {missing === 1 ? "Falta 1." : `Faltam ${missing}.`}
          </p>
        </div>
      ) : error ? (
        <div class="empty rd-sheet-msg">
          <p class="empty-title">Não deu para procurar agora.</p>
          <p class="empty-line">Confira a conexão e tente de novo.</p>
        </div>
      ) : !ids ? (
        <p class="rd-loading" role="status">
          <IconSpin />
          Procurando imagens com a mesma atmosfera…
        </p>
      ) : !list.length ? (
        <div class="empty rd-sheet-msg">
          <p class="empty-title">Nada parecido por enquanto.</p>
          <p class="empty-line">Se a análise desta imagem acabou de terminar, tente de novo daqui a pouco.</p>
        </div>
      ) : (
        <>
          <p class="sr-only" role="status">
            {list.length === 1 ? "1 imagem parecida" : `${list.length} imagens parecidas`}
          </p>
          <Grid cards={list} lead={false} resetKey={c.id} density="medio" />
        </>
      )}
    </dialog>
  );
}
